import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Styles/SummaryPage.css';

const SummaryPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [hasPlayedSummary, setHasPlayedSummary] = useState(false);
  const [cartNotes, setCartNotes] = useState({});

  const isSummaryInProgress = useRef(false);
  const recognitionRef = useRef(null);
  const confirmedOrder = useRef(false);
  const navigate = useNavigate();


const speakText = async (text) => {
  // Always reset the speaking flag at the start
  window.isSpeaking = false;
  
  const audioEnabledState = sessionStorage.getItem('audioEnabled') === 'true';
  
  if (!audioEnabledState) {

    return;
  }
  
  try {
    // Cancel any existing speech first
    window.speechSynthesis.cancel();

    window.isSpeaking = true;
    
    const response = await fetch('https://waiter-ai.onrender.com/api/tts/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sentence: text })
    });
    
    if (response.ok) {
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      return new Promise((resolve) => {
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          window.isSpeaking = false;
          resolve();
        };
        
        audio.onerror = (error) => {
          console.error('Audio playback error:', error);
          URL.revokeObjectURL(audioUrl);
          window.isSpeaking = false;
          resolve();
        };
        
        audio.play().catch(error => {
          console.error('Audio play failed:', error);
          window.isSpeaking = false;
          resolve();
        });
      });
    } else {
      console.error('TTS request failed:', response.status);
      window.isSpeaking = false;
      return;
    }
    
  } catch (error) {
    console.error('TTS error:', error);
    window.isSpeaking = false;
    return;
  }
};

  // Load order data from sessionStorage
useEffect(() => {
  const loadOrderData = () => {
    try {
      const savedCart = sessionStorage.getItem('currentOrder');
      const savedNotes = sessionStorage.getItem('cartNotes');
      const sessionData = sessionStorage.getItem('orderSession');
      
      if (!savedCart) {
        navigate('/order');
        return;
      }

      const cart = JSON.parse(savedCart);
      const cartNotes = savedNotes ? JSON.parse(savedNotes) : {};
      const session = sessionData ? JSON.parse(sessionData) : {};
      
      const storedProducts = localStorage.getItem('products');
      if (storedProducts) {
        const productsData = JSON.parse(storedProducts);
        setProducts(productsData);
        fetchVariants();
      }

      // Parse cart items - handle both variant and non-variant products, including notes
      const orderItems = Object.entries(cart).map(([cartKey, quantity]) => {
        
        // First, check if this cart key has notes suffix
        let baseCartKey = cartKey;
        let hasNotes = false;
        const notesPattern = /-notes-[^-]+$/;
        
        if (notesPattern.test(cartKey)) {
          baseCartKey = cartKey.replace(notesPattern, '');
          hasNotes = true;
        }
        
        // Now count hyphens in the base cart key
        const hyphenCount = (baseCartKey.match(/-/g) || []).length;
        
        if (hyphenCount === 9) {
          // Product with variant (UUID-UUID format)
          const parts = baseCartKey.split('-');
          if (parts.length === 10) {
            const productId = parts.slice(0, 5).join('-');
            const variantId = parts.slice(5, 10).join('-');
            
            return {
              cartKey,
              productId,
              variantId,
              quantity,
              hasVariant: true,
              notes: hasNotes ? cartNotes[cartKey] : null
            };
          }
        } else if (hyphenCount === 4) {
          // Product without variant (just UUID)
          return {
            cartKey,
            productId: baseCartKey,
            variantId: null,
            quantity,
            hasVariant: false,
            notes: hasNotes ? cartNotes[cartKey] : null
          };
        }
        
        console.warn('Unrecognized cart key format:', cartKey);
        return null;
      }).filter(Boolean);


      setOrderData({
        invoice: {
          sessionId: session.sessionId,
          timestamp: session.timestamp,
          restaurantName: session.restaurantName,
          rawOrder: orderItems
        }
      });
      
      // Store cartNotes in state too
      setCartNotes(cartNotes);

      setLoading(false);
      
    } catch (error) {
      console.error('Error loading order data:', error);
      navigate('/order');
    }
  };

  loadOrderData();
}, [navigate]);

  // Fetch variants data
  const fetchVariants = async () => {
    try {
      const restaurantId = sessionStorage.getItem('restaurantId');
      const menuUrl = restaurantId
        ? `https://waiter-ai.onrender.com/api/orders/get_menu/${restaurantId}`
        : 'https://waiter-ai.onrender.com/api/orders/get_menu';
      const response = await fetch(menuUrl);
      if (response.ok) {
        const result = await response.json();
        setVariants(result.varients || []);
      }
    } catch (error) {
      console.error('Error fetching variants:', error);
    }
  };

  // Get product and variant details
const getOrderDetails = () => {
  if (!orderData || !products.length) return [];

  return orderData.invoice.rawOrder.map((item, index) => {
    const product = products.find(p => p.id === item.productId);
    
    if (!product) {
      console.warn('Product not found for ID:', item.productId);
      return null;
    }

    if (item.hasVariant && item.variantId) {
      // Product with variant
      const variant = variants.find(v => v.id === item.variantId);
      if (variant) {
        return {
          index: index + 1,
          type: product.name,
          size: variant.name,
          quantity: item.quantity,
          price: variant.price,
          subtotal: variant.price * item.quantity,
          notes: item.notes || 'None', // Use notes from item
          hasVariant: true
        };
      } else {
        return null;
      }
    } else {
      // Product without variant
      return {
        index: index + 1,
        type: product.name,
        size: null,
        quantity: item.quantity,
        price: product.price,
        subtotal: product.price * item.quantity,
        notes: item.notes || 'None', // Use notes from item
        hasVariant: false
      };
    }
  }).filter(Boolean);
};
  // Calculate totals
  const calculateTotals = () => {
    const orderDetails = getOrderDetails();
    const subtotal = orderDetails.reduce((sum, item) => sum + item.subtotal, 0);
    const tax = subtotal * 0.08;
    const total = subtotal + tax;
    
    return { subtotal, tax, total };
  };

  // Process voice input for changes confirmation
  const processVoiceInput = async (text) => {

    // Check if order is already confirmed - prevent further processing
    if (confirmedOrder.current) {
      return;
    }
    
    // Check for affirmative responses (want to make changes)
    const affirmativeWords = ['yes', 'yep', 'yeah', 'yup', 'sure', 'okay', 'ok', 'change', 'edit', 'modify'];
    const isAffirmative = affirmativeWords.some(word => text.includes(word));
    
    // Check for negative responses (confirm order)
    const negativeWords = ['no', 'nope', 'nah', 'confirm', 'good', 'correct', 'right', 'done', 'finish'];
    const isNegative = negativeWords.some(word => text.includes(word));
    
    if (isAffirmative) {
      const qr = sessionStorage.getItem('qrCode')
      navigate('/order/'+qr);
      
    } else if (isNegative) {
      confirmedOrder.current = true; // Set flag immediately to prevent duplicate processing
      
      try {
        // Calculate totals to get the price
        const totals = calculateTotals();
        
        const restaurantId = sessionStorage.getItem('restaurantId');
        const confirmUrl = restaurantId
          ? `https://waiter-ai.onrender.com/api/orders/confirm_order/${restaurantId}`
          : 'https://waiter-ai.onrender.com/api/orders/confirm_order';
        const response = await fetch(confirmUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            rawOrder: orderData.invoice.rawOrder, 
            price: totals.total // Fixed: use calculated total price
          })
        });
        
        if (response.ok) {
          await speakText('Great! Your order has been confirmed and sent to the kitchen.');
        } else {
          console.error('Order confirmation failed:', response.status);
        }
        
      } catch (error) {
        console.error('Server Error: ', error);
      } finally {
        setTimeout(() => {
          handleConfirmOrder();
        }, 2000);
      }
      
    } else {
      
      await speakText('I didn\'t understand. Please say yes to make changes or no to confirm your order.');
      setTimeout(() => {
        startVoiceListening();
      }, 2000);
    }
  };

  // Voice recognition functions
  const startVoiceListening = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        const speechResult = event.results[0][0].transcript.toLowerCase();
        processVoiceInput(speechResult);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.start();
    }
  };

  const stopVoiceListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };


  // TTS Summary function
  const informSummary = async () => {
    if (isSummaryInProgress.current) return;
    
    isSummaryInProgress.current = true;
    
    try {
      const orderDetails = getOrderDetails();
      let summaryText = "";
      if (orderDetails.length === 1) {
        const item = orderDetails[0];
        summaryText += `You ordered ${item.quantity} `;
        
        if (item.hasVariant && item.size) {
          summaryText += `${item.size} `;
        }
        
        summaryText += item.type;
        
        if (item.notes && item.notes !== 'None') {
          const notesText = Array.isArray(item.notes) ? item.notes.join(', ') : item.notes;
          summaryText += `, ${notesText}`;
        }
        
        summaryText += ". ";
      } else {
        orderDetails.forEach((item, index) => {
          if (index === orderDetails.length - 1) {
            summaryText += "And finally, ";
          } 
          
          summaryText += `${item.quantity} `;
          
          if (item.hasVariant && item.size) {
            summaryText += `${item.size} `;
          }
          
          summaryText += item.type;
          
          if (item.notes && item.notes !== 'None') {
            const notesText = Array.isArray(item.notes) ? item.notes.join(', ') : item.notes;
            summaryText += `, ${notesText}`;
          }
          
          summaryText += ". ";
        });
      }
    
      const totals = calculateTotals();
      summaryText += `Your total comes to $${totals.total.toFixed(2)}. Would you like to make any changes?`;
    
    
      // Use browser TTS
      await speakText(summaryText);
      
      // Start listening after TTS finishes
      setTimeout(() => {
        startVoiceListening();
      }, 500);

     
      } catch (error) {
        console.error('Error during summary TTS:', error);
        // Fallback: start listening even if TTS fails
        setTimeout(() => {
          startVoiceListening();
        }, 1000);
      } finally {
        isSummaryInProgress.current = false;
      }
  };

    // Handle confirm order
  const handleConfirmOrder = () => {
    
    // Stop any voice recognition
    stopVoiceListening();
    
    // Navigate to waiting page immediately
    navigate('/waiting');
    
    // Clear all session data after navigation
    setTimeout(() => {
      // Clear everything including the welcome flag
      sessionStorage.clear();
    }, 1000); // Clear after 1 second
  };

  // Handle edit order
  const handleEditOrder = () => {
    stopVoiceListening();
    navigate('/order');
  };

  // Cleanup voice recognition on component unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);

    if (orderData && products.length > 0 && !hasPlayedSummary && !isSummaryInProgress.current) {
      // For products without variants, we don't need to wait for variants to load
      const needsVariants = orderData.invoice.rawOrder.some(item => item.hasVariant);
      
      if (!needsVariants || variants.length > 0) {
        setHasPlayedSummary(true);
        informSummary();
      }
    }
  }, [orderData, products, variants, hasPlayedSummary]);

  if (loading) {
    return (
      <div className="order-summary-container">
        <div className="order-summary-card">
          <div className="loading">Loading order summary...</div>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="order-summary-container">
        <div className="order-summary-card">
          <div className="error">No order data found. Please place an order first.</div>
          <button onClick={() => navigate('/waiting')} className="btn btn-primary">
            Continue
          </button>
        </div>
      </div>
    );
  }

  const orderDetails = getOrderDetails();
  const totals = calculateTotals();

  return (
    <div className="order-summary-container">
      <div className={`order-summary-card ${isVisible ? 'visible' : ''}`}>
        {/* Header */}
        <div className="order-summary-header">
          <h1 className="order-summary-title">Order Summary</h1>
          {orderData.invoice.restaurantName && (
            <div className="invoice-id">
              {orderData.invoice.restaurantName}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className="order-details">
          {orderDetails.map((item, index) => (
            <div key={index} className={`order-item ${index < orderDetails.length - 1 ? 'item-border' : ''}`}>
              
              <div className="detail-row">
                <span className="detail-label">Item:</span>
                <span className="detail-value item-type">
                  {item.type}
                </span>
              </div>

              {/* Only show size if product has variants */}
              {item.hasVariant && item.size && (
                <div className="detail-row">
                  <span className="detail-label">Size/Type:</span>
                  <span className="size-badge">
                    {item.size}
                  </span>
                </div>
              )}

              <div className="detail-row">
                <span className="detail-label">Quantity:</span>
                <span className="quantity-badge">
                  {item.quantity}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Price:</span>
                <span className="detail-value">
                  ${item.price.toFixed(2)}
                </span>
              </div>

              <div className="detail-row last-row">
                <span className="detail-label">Special Notes:</span>
                <span className="detail-value notes-text">
                  {item.notes}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Order Totals */}
        <div className="order-totals">
          <div className="total-row">
            <span>Subtotal:</span>
            <span>${totals.subtotal.toFixed(2)}</span>
          </div>
          <div className="total-row">
            <span>Tax (8.0%):</span>
            <span>${totals.tax.toFixed(2)}</span>
          </div>
          <div className="total-row final-total">
            <span>Total:</span>
            <span>${totals.total.toFixed(2)}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="button-container">
          <button 
            className="btn btn-primary"
            onClick={handleConfirmOrder}
          >
            Confirm Order
          </button>
          <button 
            className="btn btn-secondary"
            onClick={handleEditOrder}
          >
            Edit Order
          </button>
        </div>
      </div>

      {/* Voice Controls - Outside the card */}
      <div className="voice-controls-external">
        <button 
          className={`summary-voice-btn ${isListening ? 'listening' : ''}`}
          onClick={isListening ? stopVoiceListening : startVoiceListening}
          title={isListening ? "Stop listening" : "Start voice control"}
        >
          <svg 
            className={isListening ? 'pulse-icon' : ''} 
            width="26" 
            height="26" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            <line x1="12" y1="19" x2="12" y2="23"></line>
            <line x1="8" y1="23" x2="16" y2="23"></line>
          </svg>
        </button>
        
      </div>
    </div>
  );
};

export default SummaryPage;